import type { MessageView, RequestDetail, RequestPanel, TurnLane } from "@procuremate/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiRequestError } from "@/lib/api-client";
import { requestKeys, useApplyPanel } from "./api";
import { streamTurn } from "./turn-stream";

/** How long the panel keeps refreshing after a turn that may have queued the background review. */
export const REVIEW_WATCH_MS = 120_000;
export const REVIEW_POLL_MS = 15_000;

/** The turn being written: the requester's message (sent) and the reply so far. */
export interface PendingTurn {
  text: string;
  /** Set by `turn.started`: the message is stored. */
  userMessageId: string | null;
  lane: TurnLane | null;
  reply: string;
}

/**
 * Why the last send did not complete. `failed`: the server answered with its localised failure
 * text (already shown in the conversation); the others need a message from the web app.
 */
export type TurnFailure = { kind: "failed" } | { kind: "busy" } | { kind: "rejected" } | { kind: "network" };

/**
 * One conversation turn at a time over POST /api/requests/:id/turns (SSE). Completed messages
 * live in the cached request (so a refetch and a turn agree); the turn in progress is local.
 */
export function useConversation(requestId: string) {
  const queryClient = useQueryClient();
  const applyPanel = useApplyPanel(requestId);
  const [pending, setPending] = useState<PendingTurn | null>(null);
  const [failure, setFailure] = useState<TurnFailure | null>(null);
  /** The last complete reply, for the screen-reader live region (deltas are not announced). */
  const [announcement, setAnnouncement] = useState("");
  /** The last turn's model hint (ADR-0014): highlight Review & submit; it never submits. */
  const [suggestSubmit, setSuggestSubmit] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const reviewWatchUntil = useRef(0);

  // Leaving the page stops reading the stream; the server still finishes and saves the turn.
  useEffect(() => () => abortRef.current?.abort(), []);

  const appendMessages = useCallback(
    (messages: MessageView[]) =>
      queryClient.setQueryData<RequestDetail>(requestKeys.detail(requestId), (old) => {
        if (!old) return old;
        const known = new Set(old.messages.map((m) => m.id));
        return { ...old, messages: [...old.messages, ...messages.filter((m) => !known.has(m.id))] };
      }),
    [queryClient, requestId],
  );

  const resync = useCallback(
    () => queryClient.invalidateQueries({ queryKey: requestKeys.detail(requestId) }),
    [queryClient, requestId],
  );

  /** Sends one message. Resolves true when the reply is complete; false means the text should go back to the composer. */
  const send = useCallback(
    async (text: string): Promise<boolean> => {
      if (abortRef.current) return false;
      const controller = new AbortController();
      abortRef.current = controller;
      setFailure(null);
      setPending({ text, userMessageId: null, lane: null, reply: "" });
      await queryClient.cancelQueries({ queryKey: requestKeys.detail(requestId) });

      let reply = "";
      let lane: TurnLane | null = null;
      let panel: RequestPanel | null = null;
      let outcome: "done" | "error" | null = null;
      const now = () => new Date().toISOString();
      try {
        await streamTurn(
          requestId,
          text,
          (event) => {
            switch (event.event) {
              case "turn.started":
                lane = event.data.lane;
                setPending((p) => p && { ...p, userMessageId: event.data.userMessageId, lane: event.data.lane });
                break;
              case "reply.delta":
                reply += event.data.text;
                setPending((p) => p && { ...p, reply: p.reply + event.data.text });
                break;
              case "case.updated":
                panel = event.data;
                applyPanel(event.data);
                break;
              case "turn.done":
                outcome = "done";
                setSuggestSubmit(event.data.suggestSubmit);
                appendMessages([
                  { id: event.data.userMessageId, role: "USER", content: text, status: "COMPLETE", createdAt: now() },
                  {
                    id: event.data.assistantMessageId,
                    role: "ASSISTANT",
                    content: reply,
                    status: "COMPLETE",
                    createdAt: now(),
                  },
                ]);
                setAnnouncement(reply);
                break;
              case "turn.error":
                outcome = "error";
                if (event.data.userMessageId) {
                  appendMessages([
                    { id: event.data.userMessageId, role: "USER", content: text, status: "COMPLETE", createdAt: now() },
                    {
                      id: `failed-${event.data.userMessageId}`,
                      role: "ASSISTANT",
                      content: event.data.message,
                      status: "FAILED",
                      createdAt: now(),
                    },
                  ]);
                }
                setAnnouncement(event.data.message);
                setFailure({ kind: "failed" });
                break;
            }
          },
          controller.signal,
        );
        if (outcome === null) setFailure({ kind: "network" });
      } catch (error) {
        if (controller.signal.aborted) return false;
        if (error instanceof ApiRequestError && error.status === 409) {
          // Either another turn is running or the request is no longer open: the refetch tells which.
          setFailure({ kind: "busy" });
        } else if (error instanceof ApiRequestError && error.status < 500) {
          setFailure({ kind: "rejected" });
        } else {
          setFailure({ kind: "network" });
        }
      } finally {
        abortRef.current = null;
        setPending(null);
      }
      // The server's copy wins (ids of failed replies, the exact stored text).
      void resync();
      if (outcome === "done" && lane === "full" && (panel as RequestPanel | null)?.gate.canSubmit) {
        reviewWatchUntil.current = Date.now() + REVIEW_WATCH_MS;
      }
      return outcome === "done";
    },
    [applyPanel, appendMessages, queryClient, requestId, resync],
  );

  /** For useRequestDetail: poll while a background review may still add findings, never mid-turn. */
  const refetchInterval = useCallback(
    () => (!abortRef.current && Date.now() < reviewWatchUntil.current ? REVIEW_POLL_MS : false),
    [],
  );

  return {
    send,
    pending,
    failure,
    clearFailure: () => setFailure(null),
    announcement,
    suggestSubmit,
    refetchInterval,
  };
}
